// Shared MLB bullpen helpers used by MatchupsPanel + PlayerOddsTable so the
// dropdown construction stays in one place.

// Build a { pitcherId → 1-based rank } map from the probable-pitcher history
// on the game doc. The array is chronological (append log — last entry is
// the current probable), so:
//   rank 1 = current probable starter
//   rank 2 = the previous probable starter (usually the "real" starter
//             behind an opener when this exists at all)
//
// Primary shape observed in the DB is a transition record per entry:
//   { from: {id, fullName, throws} | null, to: {id, fullName, throws}, changedAt }
// We read the `to.id` of each entry (the pitcher who became probable at that
// step). Also tolerates simple id-only shapes for forward compatibility:
//   game.{home,away}ProbablePitcherHistory: [id | {id|pitcherId|personId|playerId|person.id}]
//   game.probablePitcherHistory: { home: [...], away: [...] }
// Returns {} when nothing is available so lookups just no-op.
export function buildProbablePitcherRank(gameDoc, isHomeSide) {
  if (!gameDoc) return {};
  const perTeamKey = isHomeSide ? 'home' : 'away';
  const arr = gameDoc[`${perTeamKey}ProbablePitcherHistory`]
    || gameDoc.probablePitcherHistory?.[perTeamKey]
    || null;
  if (!Array.isArray(arr) || arr.length === 0) return {};

  const toNumericId = (raw) => {
    if (raw == null) return null;
    const n = parseInt(String(raw).replace(/^mlb_/, ''));
    return Number.isFinite(n) ? n : null;
  };
  const extractId = (item) => {
    if (item == null) return null;
    // Transition-record shape: prefer `to` (the pitcher listed after the change).
    if (item.to != null) {
      const to = item.to;
      const raw = (typeof to === 'number' || typeof to === 'string')
        ? to
        : (to?.id ?? to?.pitcherId ?? to?.personId ?? to?.playerId ?? to?.person?.id);
      const n = toNumericId(raw);
      if (n != null) return n;
    }
    // Bare id or flat pitcher-object shape (legacy / fallback).
    const raw = (typeof item === 'number' || typeof item === 'string')
      ? item
      : (item?.id ?? item?.pitcherId ?? item?.personId ?? item?.playerId ?? item?.person?.id);
    return toNumericId(raw);
  };

  const rank = {};
  // Iterate newest → oldest so rank 1 lands on the current probable.
  for (let i = arr.length - 1, r = 1; i >= 0; i--, r++) {
    const num = extractId(arr[i]);
    if (num != null && rank[num] == null) rank[num] = r;
  }
  return rank;
}

// Returns true when the probable-pitcher history for a team side has 2+
// transitions — i.e. the currently-listed probable has already been swapped
// in behind an earlier probable. That's the tell-tale sign of a potential
// opener situation (an opener listed to eat the first inning, with the real
// starter previously listed and now bumped behind them).
export function isPotentialOpener(gameDoc, isHomeSide) {
  if (!gameDoc) return false;
  const perTeamKey = isHomeSide ? 'home' : 'away';
  const arr = gameDoc[`${perTeamKey}ProbablePitcherHistory`]
    || gameDoc.probablePitcherHistory?.[perTeamKey]
    || null;
  return Array.isArray(arr) && arr.length >= 2;
}

// Extract the previous probable pitcher (rank 2) as { id, fullName, throws }.
// That's the pitcher who was listed as probable just before the current one
// — usually the true starter behind an opener. Returns null when there's no
// history entry describing a previous probable.
//
// The primary shape is transition records ({ from, to, changedAt }); we read
// the `from` of the last entry (the pitcher who was replaced). Falls back to
// the `to` of the second-to-last entry when `from` is missing / null. Bare-id
// shapes are also tolerated for forward compatibility.
export function getPreviousProbablePitcher(gameDoc, isHomeSide) {
  if (!gameDoc) return null;
  const perTeamKey = isHomeSide ? 'home' : 'away';
  const arr = gameDoc[`${perTeamKey}ProbablePitcherHistory`]
    || gameDoc.probablePitcherHistory?.[perTeamKey]
    || null;
  if (!Array.isArray(arr) || arr.length < 2) return null;
  const last = arr[arr.length - 1];
  const second = arr[arr.length - 2];
  const asPitcher = (item) => {
    if (!item) return null;
    // Full pitcher object with fullName.
    if (item.fullName || item.name) {
      return {
        id: item.id ?? item.pitcherId ?? item.personId ?? item.playerId ?? item?.person?.id ?? null,
        fullName: item.fullName || item.name,
        throws: item.throws || null,
      };
    }
    return null;
  };
  // Prefer `from` on the latest transition — that's the pitcher who was bumped.
  const fromLast = last?.from;
  if (fromLast) {
    const p = asPitcher(fromLast);
    if (p?.fullName) return p;
  }
  // Fall back to `to` on the second-to-last transition.
  const toSecond = second?.to;
  if (toSecond) {
    const p = asPitcher(toSecond);
    if (p?.fullName) return p;
  }
  return null;
}

// Build the bullpen list for one side of a game. Each entry is:
//   { id, name, hand, rank, avgEnteredInning, daysSinceLastAppearance,
//     inningsPitched, role }
// Order:
//   1. Current probable starter (from home/awayProbablePitcher).
//   2. Previous probable (rank 2) when the current probable is a potential
//      opener — that's usually the "true" starter behind an opener.
//   3. The rest of the bullpen roster (dedup'd against #1 / #2).
// Skips entries that can't be resolved to a name via `{home,away}Players`
// falling back to the pitcher object from history for name/hand when the
// roster map doesn't have them (e.g. non-bullpen starters).
// The `avgEnteredInning` / `daysSinceLastAppearance` / `inningsPitched` /
// `role` fields come from `game.{home,away}BullpenData`; they'll be `null`
// for entries that aren't in the bullpen (like a non-relief starter).
export function buildBullpenList(gameDoc, isHomeSide) {
  if (!gameDoc) return [];
  const players = isHomeSide ? gameDoc.homePlayers : gameDoc.awayPlayers;
  const bullpenIds = isHomeSide ? gameDoc.homeBullpen : gameDoc.awayBullpen;
  const bullpenData = isHomeSide ? gameDoc.homeBullpenData : gameDoc.awayBullpenData;
  const dataById = {};
  if (Array.isArray(bullpenData)) {
    for (const entry of bullpenData) {
      const key = parseInt(String(entry?.playerId).replace(/^mlb_/, ''));
      if (Number.isFinite(key)) dataById[key] = entry;
    }
  }
  const rank = buildProbablePitcherRank(gameDoc, isHomeSide);

  // Build a list entry from a raw id, with an optional pitcher-object
  // fallback (used when the roster map doesn't have the player, e.g. the
  // "true" starter that isn't in the bullpen array).
  const buildEntry = (rawId, fallback) => {
    const numericId = parseInt(String(rawId).replace(/^mlb_/, ''));
    if (!Number.isFinite(numericId)) return null;
    const info = players?.[`ID${numericId}`];
    const name = info?.person?.fullName || fallback?.fullName || fallback?.name || null;
    const hand = info?.pitchHand?.code || fallback?.throws || null;
    if (!name) return null;
    const data = dataById[numericId] || null;
    return {
      id: numericId,
      name,
      hand,
      rank: rank[numericId] || null,
      avgEnteredInning: data?.avgEnteredInning ?? null,
      daysSinceLastAppearance: data?.daysSinceLastAppearance ?? null,
      inningsPitched: data?.inningsPitched ?? null,
      role: data?.role || null,
    };
  };

  const seenIds = new Set();
  const result = [];
  const push = (entry) => {
    if (!entry || seenIds.has(entry.id)) return;
    seenIds.add(entry.id);
    result.push(entry);
  };

  // 1. Current probable starter first.
  const starter = isHomeSide ? gameDoc.homeProbablePitcher : gameDoc.awayProbablePitcher;
  if (starter?.id != null) push(buildEntry(starter.id, starter));

  // 2. Projected #2 next when the current probable is a potential opener.
  if (isPotentialOpener(gameDoc, isHomeSide)) {
    const prev = getPreviousProbablePitcher(gameDoc, isHomeSide);
    if (prev?.id != null) push(buildEntry(prev.id, prev));
  }

  // 3. Rest of the bullpen roster — sorted by `avgEnteredInning` ascending
  //    (earlier entries in the game come first). Pitchers without an
  //    `avgEnteredInning` value sort to the end.
  if (Array.isArray(bullpenIds)) {
    const entries = bullpenIds
      .map(id => buildEntry(id, null))
      .filter(e => e && !seenIds.has(e.id));
    entries.sort((a, b) => {
      const aVal = typeof a.avgEnteredInning === 'number' ? a.avgEnteredInning : Infinity;
      const bVal = typeof b.avgEnteredInning === 'number' ? b.avgEnteredInning : Infinity;
      return aVal - bVal;
    });
    for (const entry of entries) push(entry);
  }

  return result;
}
